import { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Droplets, Thermometer, Wind, Sun, AlertTriangle, Check, Cloud, ArrowUp, ArrowDown, Minus, LineChart as LineChartIcon, DownloadCloud } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { WeatherCard } from '@/components/greenhouse/WeatherCard';
import { api } from '@/lib/api';
import { tbApi } from '@/lib/tbApi';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useTelemetry } from '@/hooks/useTelemetry';

interface HydroGreenhousePageProps {
  project: string;
  gh: string;
  greenhouseId: number;
  greenhouseName: string;
}

interface SensorConfigInfo {
  sensorKey: string;
  dataKey: string;
  unit: string;
  minValue: number | null;
  maxValue: number | null;
  alertMin: number | null;
  alertMax: number | null;
}

interface HydroReading {
  id: number;
  greenhouse_id: number;
  recorded_at: string;
  ec: number | null;
  tds: number | null;
  ph: number | null;
  water_temp: number | null;
  air_temp: number | null;
  humidity: number | null;
  lux: number | null;
  co2: number | null;
}

function fmtInt(v: number | null) {
  if (v === null) return '--';
  return Math.round(v).toLocaleString();
}
function fmt1(v: number | null) {
  if (v === null) return '--';
  return v.toFixed(1);
}

function downsample<T>(arr: T[], maxPoints: number): T[] {
  if (arr.length <= maxPoints) return arr;
  const step = arr.length / maxPoints;
  const out: T[] = [];
  for (let i = 0; i < maxPoints; i++) out.push(arr[Math.floor(i * step)]);
  return out;
}

/* ─── SPARKLINE ─── */
function Sparkline({ points, colorRgb, width = 84, height = 30 }: { points: number[]; colorRgb: string; width?: number; height?: number }) {
  const clean = points.filter((v) => typeof v === 'number' && !Number.isNaN(v));
  if (clean.length < 2) return <div style={{ width, height }} />;

  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const range = max - min || 1;
  const stepX = width / (clean.length - 1);
  const coords = clean.map((v, i) => [i * stepX, height - ((v - min) / range) * (height - 4) - 2] as const);
  const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const fill = `${line} L${width},${height} L0,${height} Z`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <path d={fill} fill={`rgba(${colorRgb},0.12)`} />
      <path d={line} fill="none" stroke={`rgb(${colorRgb})`} strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={coords[coords.length - 1][0]} cy={coords[coords.length - 1][1]} r={2.5} fill={`rgb(${colorRgb})`} />
    </svg>
  );
}

/* ─── TREND BADGE (delta vs previous point) ─── */
function TrendBadge({ points, decimals, unit }: { points: number[]; decimals: number; unit: string }) {
  const clean = points.filter((v) => typeof v === 'number' && !Number.isNaN(v));
  if (clean.length < 2) return null;
  const delta = clean[clean.length - 1] - clean[clean.length - 2];
  const flat = Math.abs(delta) < 10 ** -decimals / 2;
  const Icon = flat ? Minus : delta > 0 ? ArrowUp : ArrowDown;
  const colorClass = flat
    ? 'text-gray-400 dark:text-gray-500'
    : delta > 0
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-rose-600 dark:text-rose-400';
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-[11px] font-medium', colorClass)}>
      <Icon size={11} strokeWidth={2.5} />
      {flat ? 'คงที่' : `${delta > 0 ? '+' : ''}${delta.toFixed(decimals)}${unit}`}
    </span>
  );
}

/* ─── LIQUID WAVE CARD ─── */
function LiquidCard({
  label, sublabel, value, display, unit, colorRgb, borderClass, min, max, rangeLabel, series, decimals,
}: {
  label: string; sublabel: string; value: number | null; display: string; unit: string;
  colorRgb: string; borderClass: string; min?: number; max?: number; rangeLabel?: string;
  series: number[]; decimals: number;
}) {
  const hasRange = min !== undefined && max !== undefined;
  const isWarn = hasRange && value !== null && (value < min! || value > max!);

  return (
    <div className={cn(
      'relative overflow-hidden rounded-2xl h-[188px] border-2 bg-white dark:bg-gray-900 transition-shadow hover:shadow-lg',
      borderClass
    )}>
      <div className="absolute inset-0 z-[2] flex flex-col gap-0.5 p-3.5">
        <div className="flex items-center justify-between mb-0.5">
          <div className="text-[13px] font-bold tracking-wide" style={{ color: `rgb(${colorRgb})` }}>{label}</div>
          {(hasRange || isWarn) && (
            <div className={cn(
              'flex items-center gap-1 rounded-full px-2 py-0.5',
              isWarn ? 'bg-amber-500/15' : 'bg-emerald-500/15'
            )}>
              {isWarn
                ? <AlertTriangle size={10} className="text-amber-700 dark:text-amber-400" />
                : <Check size={10} className="text-emerald-700 dark:text-emerald-400" />}
              <span className={cn('text-[10px] font-semibold', isWarn ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400')}>
                {isWarn ? 'ออกนอกเกณฑ์' : 'ปกติ'}
              </span>
            </div>
          )}
        </div>
        <div className="text-[11px] text-gray-400 dark:text-gray-500">{sublabel}</div>

        <div className="mt-auto flex items-end justify-between gap-2">
          <div>
            <div className="flex items-baseline gap-1">
              <span
                className="font-bold leading-none tracking-tight tabular-nums"
                style={{ fontSize: display.length > 5 ? 30 : 38, color: value === null ? undefined : `rgb(${colorRgb})` }}
              >
                {display}
              </span>
              {value !== null && unit && (
                <span className="text-xs font-medium opacity-80" style={{ color: `rgb(${colorRgb})` }}>{unit}</span>
              )}
            </div>
            <TrendBadge points={series} decimals={decimals} unit={unit} />
          </div>
          <Sparkline points={series} colorRgb={colorRgb} />
        </div>
        {hasRange && (
          <div className="mt-1 text-[10px] font-medium opacity-70" style={{ color: `rgb(${colorRgb})` }}>
            {rangeLabel || `เกณฑ์ ${min}–${max} ${unit}`}
          </div>
        )}
      </div>

      {/* animated wave at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-[110px] overflow-hidden z-[1]">
        <div className="absolute inset-x-0 bottom-0 top-[20px]" style={{ background: `rgba(${colorRgb},0.08)` }} />
        <svg viewBox="0 0 600 30" preserveAspectRatio="none" className="absolute top-0 left-0 w-[200%] h-[30px] block animate-[wave1_4s_linear_infinite]">
          <path d="M0,15 C75,5 150,25 225,12 C300,2 375,22 450,10 C525,0 600,18 600,10 L600,30 L0,30 Z" fill={`rgba(${colorRgb},0.16)`} />
        </svg>
        <svg viewBox="0 0 600 30" preserveAspectRatio="none" className="absolute top-[6px] left-0 w-[200%] h-[30px] block animate-[wave2_5s_linear_infinite]">
          <path d="M0,18 C75,8 150,26 225,15 C300,5 375,24 450,13 C525,3 600,20 600,13 L600,30 L0,30 Z" fill={`rgba(${colorRgb},0.22)`} />
        </svg>
      </div>
    </div>
  );
}

/* ─── ENV CARD (big number + gauge + sparkline) ─── */
function EnvCard({
  label, sublabel, value, display, unit, icon, colorRgb, rangeMin, rangeMax, series, decimals, min, max,
}: {
  label: string; sublabel: string; value: number | null; display: string; unit: string;
  icon: React.ReactNode; colorRgb: string; rangeMin: number; rangeMax: number;
  series: number[]; decimals: number; min?: number; max?: number;
}) {
  const pct = value === null ? 0 : Math.min(100, Math.max(0, ((value - rangeMin) / (rangeMax - rangeMin)) * 100));
  const isWarn = value !== null && ((min !== undefined && value < min) || (max !== undefined && value > max));

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 transition-shadow hover:shadow-lg">
      <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl" style={{ background: `rgb(${colorRgb})` }} />
      <div className="pl-2">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs font-semibold text-gray-600 dark:text-gray-300">{label}</div>
            <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{sublabel}</div>
          </div>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `rgba(${colorRgb},0.12)`, color: `rgb(${colorRgb})` }}>
            {icon}
          </div>
        </div>

        <div className="flex items-end justify-between gap-2 mb-3">
          <div>
            <div className="flex items-baseline gap-1">
              <span className="text-[34px] font-bold leading-none tracking-tight tabular-nums" style={{ color: value === null ? undefined : `rgb(${colorRgb})` }}>
                {display}
              </span>
              {value !== null && <span className="text-sm opacity-70" style={{ color: `rgb(${colorRgb})` }}>{unit}</span>}
            </div>
            {isWarn && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-400 mt-0.5">
                <AlertTriangle size={10} /> นอกเกณฑ์
              </span>
            )}
            <div><TrendBadge points={series} decimals={decimals} unit={unit} /></div>
          </div>
          <Sparkline points={series} colorRgb={colorRgb} />
        </div>

        <div className="relative h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
          <div
            className="absolute left-0 top-0 bottom-0 rounded-full transition-[width] duration-1000 ease-out"
            style={{ width: `${pct}%`, background: `linear-gradient(90deg, rgba(${colorRgb},0.4), rgb(${colorRgb}))` }}
          />
        </div>
        <div className="flex justify-between mt-1 text-[10px] text-gray-400 dark:text-gray-500">
          <span>{rangeMin}{unit}</span>
          <span>{rangeMax}{unit}</span>
        </div>
      </div>
    </div>
  );
}

/* ─── SECTION TITLE ─── */
function SectionTitle({ iconBg, iconColor, iconEl, title }: { iconBg: string; iconColor: string; iconEl: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2.5 mb-3.5">
      <div className="w-[30px] h-[30px] rounded-[10px] flex items-center justify-center" style={{ background: iconBg, color: iconColor }}>
        {iconEl}
      </div>
      <span className="text-[13px] font-bold text-gray-900 dark:text-gray-100 tracking-tight">{title}</span>
      <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
    </div>
  );
}

/* ─── HISTORY CHART (24h/7d/30d, pick one metric) ─── */
const CHART_METRICS: { key: keyof HydroReading; label: string; unit: string; colorRgb: string; decimals: number }[] = [
  { key: 'ec', label: 'EC', unit: 'µS/cm', colorRgb: '216,90,48', decimals: 0 },
  { key: 'ph', label: 'pH', unit: '', colorRgb: '24,95,165', decimals: 1 },
  { key: 'water_temp', label: 'อุณหภูมิน้ำ', unit: '°C', colorRgb: '15,110,86', decimals: 1 },
  { key: 'air_temp', label: 'อุณหภูมิอากาศ', unit: '°C', colorRgb: '153,60,29', decimals: 1 },
  { key: 'humidity', label: 'ความชื้น', unit: '%', colorRgb: '15,110,86', decimals: 1 },
  { key: 'co2', label: 'CO₂', unit: ' ppm', colorRgb: '107,63,160', decimals: 0 },
  { key: 'lux', label: 'ความเข้มแสง', unit: ' lux', colorRgb: '133,79,11', decimals: 0 },
];
const CHART_RANGES: { key: '24h' | '7d' | '30d'; label: string; hours: number }[] = [
  { key: '24h', label: '24 ชม.', hours: 24 },
  { key: '7d', label: '7 วัน', hours: 24 * 7 },
  { key: '30d', label: '30 วัน', hours: 24 * 30 },
];

function HistoryChartSection({ greenhouseId }: { greenhouseId: number }) {
  const [range, setRange] = useState<'24h' | '7d' | '30d'>('24h');
  const [metricKey, setMetricKey] = useState<keyof HydroReading>('ec');
  const [rows, setRows] = useState<HydroReading[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const rangeCfg = CHART_RANGES.find((r) => r.key === range)!;
    setIsLoading(true);
    api.get<{ readings: HydroReading[] }>(`/hydro/readings/history/${greenhouseId}?hours=${rangeCfg.hours}`)
      .then((res) => {
        if (res.success && res.data?.readings) setRows(downsample(res.data.readings, 200));
      })
      .catch((e) => console.error(e))
      .finally(() => setIsLoading(false));
  }, [greenhouseId, range]);

  const metric = CHART_METRICS.find((m) => m.key === metricKey)!;
  const chartData = useMemo(() => rows
    .map((r) => ({ ts: r.recorded_at, value: r[metric.key] as number | null }))
    .filter((d) => d.value !== null), [rows, metric.key]);

  // ป้ายบนแกน X: กระชับตามช่วงเวลาที่เลือก (24 ชม. = เวลา, 7/30 วัน = วันที่)
  const dateFmt = (ts: string) => {
    const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
    return range === '24h'
      ? d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit' });
  };

  // Tooltip: โชว์ทั้งวันที่และเวลาเสมอ ไม่ว่าจะเลือกช่วงไหน
  const fullDateFmt = (ts: string) => {
    const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
    return d.toLocaleString('th-TH', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div className="mt-2">
      <SectionTitle iconBg="#F3E8FF" iconColor="#7C3AED" iconEl={<LineChartIcon size={15} />} title="กราฟย้อนหลัง" />
      <div className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap gap-1.5">
            {CHART_METRICS.map((m) => (
              <button
                key={m.key}
                onClick={() => setMetricKey(m.key)}
                className={cn(
                  'px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors',
                  metricKey === m.key
                    ? 'text-white border-transparent'
                    : 'text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                )}
                style={metricKey === m.key ? { background: `rgb(${m.colorRgb})` } : undefined}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1 rounded-full bg-gray-100 dark:bg-gray-800 p-0.5">
            {CHART_RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={cn(
                  'px-3 py-1 rounded-full text-[11px] font-semibold transition-colors',
                  range === r.key
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400'
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="h-[220px] flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
            กำลังโหลด...
          </div>
        ) : chartData.length < 2 ? (
          <div className="h-[220px] flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
            ยังไม่มีข้อมูลเพียงพอสำหรับช่วงเวลานี้
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-gray-100 dark:stroke-gray-800" />
              <XAxis dataKey="ts" tickFormatter={dateFmt} tick={{ fontSize: 11 }} minTickGap={30} />
              <YAxis tick={{ fontSize: 11 }} width={44} domain={['auto', 'auto']} />
              <Tooltip
                labelFormatter={(ts) => fullDateFmt(ts as string)}
                formatter={(value: number) => [`${value.toFixed(metric.decimals)}${metric.unit}`, metric.label]}
              />
              <Line type="monotone" dataKey="value" stroke={`rgb(${metric.colorRgb})`} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// telemetry key ทั้งหมดที่บอร์ด Hydro ส่งจริง (ตรงกับ HYDRO_KEYS ใน server/src/services/hydroSync.ts)
const HYDRO_LIVE_KEYS = ['ec', 'ph', 'water_temp', 'air_temp', 'air_humidity', 'air_light', 'air_co2', 'fw_version'];

export function HydroGreenhousePage({ project, gh, greenhouseId }: HydroGreenhousePageProps) {
  const [history, setHistory] = useState<HydroReading[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [configByKey, setConfigByKey] = useState<Record<string, SensorConfigInfo>>({});
  const [otaState, setOtaState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const { user } = useAuth();
  const canControl = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'operator';

  // ค่าปัจจุบัน - ดึงสดจาก ThingsBoard ตรง ๆ (ไม่ผ่าน Postgres sync) เพื่อให้เรียลไทม์ตรงกับจอบอร์ด
  const { data: live, timestamps: liveTs, isLoading: isLiveLoading, refetch: refetchLive } = useTelemetry({
    project, gh, keys: HYDRO_LIVE_KEYS, pollInterval: 20000,
  });
  const fwVersion = live.fw_version as string | undefined;

  const ec = (live.ec as number | null) ?? null;
  const ph = (live.ph as number | null) ?? null;
  const waterTemp = (live.water_temp as number | null) ?? null;
  const airTemp = (live.air_temp as number | null) ?? null;
  const humidity = (live.air_humidity as number | null) ?? null;
  const co2 = (live.air_co2 as number | null) ?? null;
  const lux = (live.air_light as number | null) ?? null;

  const hasData = [ec, ph, waterTemp, airTemp, humidity, co2, lux].some((v) => v !== null);
  const isLoading = isLiveLoading || isHistoryLoading;

  const latestDeviceTs = Math.max(0, ...HYDRO_LIVE_KEYS.map((k) => liveTs[k] || 0));
  const lastUpdate = latestDeviceTs
    ? new Date(latestDeviceTs).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
    : '';

  const handleCheckOta = useCallback(async () => {
    setOtaState('sending');
    try {
      await tbApi.sendRpc(project, gh, 'checkOta', {});
      setOtaState('sent');
    } catch (e) {
      console.error(e);
      setOtaState('error');
    }
  }, [project, gh]);

  // เกณฑ์ (alert_min/alert_max, min_value/max_value) มาจากหน้า Admin > จัดการ Sensor
  // ถ้ายังไม่ได้ตั้งค่าไว้ ใช้ค่า default ที่กำหนดไว้ในโค้ดแทน
  useEffect(() => {
    api.get<{ sensors: SensorConfigInfo[] }>(`/projects/${project}/greenhouses/${gh}/sensor-configs`)
      .then((res) => {
        if (res.success && res.data?.sensors) {
          const map: Record<string, SensorConfigInfo> = {};
          for (const s of res.data.sensors) map[s.dataKey] = s;
          setConfigByKey(map);
        }
      })
      .catch((e) => console.error(e));
  }, [project, gh]);

  const threshold = useCallback((dataKey: string, fallbackAlertMin?: number, fallbackAlertMax?: number) => {
    const cfg = configByKey[dataKey];
    return {
      min: cfg?.alertMin ?? fallbackAlertMin,
      max: cfg?.alertMax ?? fallbackAlertMax,
    };
  }, [configByKey]);

  const gaugeRange = useCallback((dataKey: string, fallbackMin: number, fallbackMax: number) => {
    const cfg = configByKey[dataKey];
    return {
      rangeMin: cfg?.minValue ?? fallbackMin,
      rangeMax: cfg?.maxValue ?? fallbackMax,
    };
  }, [configByKey]);

  // เก็บไว้เฉพาะกราฟเทรนด์เล็ก ๆ (sparkline) ในการ์ด - ใช้ข้อมูลย้อนหลังจาก Postgres archive
  const fetchHistory = useCallback(async () => {
    setIsHistoryLoading(true);
    try {
      const historyRes = await api.get<{ readings: HydroReading[] }>(`/hydro/readings/history/${greenhouseId}?hours=3`);
      if (historyRes.success && historyRes.data?.readings) {
        setHistory(downsample(historyRes.data.readings, 40));
      }
    } catch (e) { console.error(e); }
    finally { setIsHistoryLoading(false); }
  }, [greenhouseId]);

  useEffect(() => {
    fetchHistory();
    const iv = setInterval(fetchHistory, 60000);
    return () => clearInterval(iv);
  }, [fetchHistory]);

  const handleRefresh = useCallback(() => {
    refetchLive();
    fetchHistory();
  }, [refetchLive, fetchHistory]);

  const series = useMemo(() => ({
    ec: history.map((h) => h.ec).filter((v): v is number => v !== null),
    ph: history.map((h) => h.ph).filter((v): v is number => v !== null),
    water_temp: history.map((h) => h.water_temp).filter((v): v is number => v !== null),
    air_temp: history.map((h) => h.air_temp).filter((v): v is number => v !== null),
    humidity: history.map((h) => h.humidity).filter((v): v is number => v !== null),
    lux: history.map((h) => h.lux).filter((v): v is number => v !== null),
    co2: history.map((h) => h.co2).filter((v): v is number => v !== null),
  }), [history]);

  return (
    <div>
      <style>{`
        @keyframes wave1 { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        @keyframes wave2 { 0%{transform:translateX(0)} 100%{transform:translateX(50%)} }
        @keyframes hydro-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.4)} }
      `}</style>

      <WeatherCard project={project} gh={gh} />
      <div className="h-4" />

      {/* status bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2.5">
          <div className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1',
            hasData ? 'bg-emerald-50 dark:bg-emerald-500/10' : 'bg-gray-100 dark:bg-gray-800'
          )}>
            <div
              className="w-[7px] h-[7px] rounded-full"
              style={{
                background: hasData ? '#22c55e' : '#9ca3af',
                animation: hasData ? 'hydro-pulse 2s infinite' : 'none',
              }}
            />
            <span className={cn('text-xs font-semibold', hasData ? 'text-emerald-700 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400')}>
              {hasData ? 'ออนไลน์' : 'ออฟไลน์'}
            </span>
          </div>
          {lastUpdate && <span className="text-xs text-gray-400 dark:text-gray-500">อัปเดต {lastUpdate} น.</span>}
          {fwVersion && <span className="text-xs text-gray-400 dark:text-gray-500">Firmware v{fwVersion}</span>}
        </div>
        <div className="flex items-center gap-2">
          {canControl && (
            <button
              onClick={handleCheckOta}
              disabled={otaState === 'sending'}
              className="flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              title="สั่งให้บอร์ดเช็คอัปเดต firmware ระยะไกล เหมือนกดปุ่มที่หน้าจอ"
            >
              <DownloadCloud size={13} className={otaState === 'sending' ? 'animate-pulse' : ''} />
              {otaState === 'sent' ? 'ส่งคำสั่งแล้ว' : otaState === 'error' ? 'ส่งไม่สำเร็จ ลองใหม่' : 'เช็คอัปเดต Firmware'}
            </button>
          )}
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw size={13} className={isLoading ? 'animate-spin' : ''} />รีเฟรช
          </button>
        </div>
      </div>
      {otaState === 'sent' && (
        <p className="text-xs text-gray-400 dark:text-gray-500 -mt-4 mb-6">
          ส่งคำสั่งไปยังบอร์ดแล้ว ถ้ามีเวอร์ชันใหม่บอร์ดจะดาวน์โหลดและรีสตาร์ทเองในไม่กี่นาที (ระหว่างนั้นจอที่หน้างานจะดับชั่วคราว) รีเฟรชหน้านี้ภายหลังเพื่อดู Firmware version ล่าสุด
        </p>
      )}

      {!hasData && !isLoading && (
        <div className="text-center py-14 px-6 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl">
          <Droplets size={36} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <p className="font-medium text-gray-600 dark:text-gray-300">ยังไม่มีข้อมูล sensor</p>
          <p className="text-[13px] text-gray-400 dark:text-gray-500 mt-1">รอ ESP32 ส่งข้อมูลเข้ามา</p>
        </div>
      )}

      {hasData && (
        <>
          <SectionTitle iconBg="#EAF3DE" iconColor="#3B6D11" iconEl={<Droplets size={15} />} title="ค่าสารละลายน้ำ" />
          <div className="grid gap-3.5 mb-7" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))' }}>
            <LiquidCard
              label="EC" sublabel="ความเข้มข้นสารละลาย" value={ec} display={fmtInt(ec)} unit="µS/cm"
              colorRgb="216,90,48" borderClass="border-orange-200 dark:border-orange-900/60"
              {...threshold('ec', 800, 3500)} series={series.ec} decimals={0}
            />
            <LiquidCard
              label="pH" sublabel="ความเป็นกรด-ด่าง" value={ph} display={fmt1(ph)} unit=""
              colorRgb="24,95,165" borderClass="border-sky-200 dark:border-sky-900/60"
              {...threshold('ph', 5.5, 7.0)} series={series.ph} decimals={1}
            />
            <LiquidCard
              label="อุณหภูมิน้ำ" sublabel="water temperature" value={waterTemp} display={fmt1(waterTemp)} unit="°C"
              colorRgb="15,110,86" borderClass="border-emerald-200 dark:border-emerald-900/60"
              {...threshold('water_temp', 18, 28)} series={series.water_temp} decimals={1}
            />
          </div>

          <SectionTitle iconBg="#E6F1FB" iconColor="#185FA5" iconEl={<Wind size={15} />} title="สภาพแวดล้อมในโรงเรือน" />
          <div className="grid gap-3.5 mb-5" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))' }}>
            <EnvCard
              label="อุณหภูมิอากาศ" sublabel="air temperature" value={airTemp} display={fmt1(airTemp)} unit="°C"
              icon={<Thermometer size={18} />} colorRgb="153,60,29" {...gaugeRange('air_temp', -40, 80)} {...threshold('air_temp')}
              series={series.air_temp} decimals={1}
            />
            <EnvCard
              label="ความชื้น" sublabel="relative humidity" value={humidity} display={fmt1(humidity)} unit="%"
              icon={<Wind size={18} />} colorRgb="15,110,86" {...gaugeRange('air_humidity', 0, 100)} {...threshold('air_humidity')}
              series={series.humidity} decimals={1}
            />
            <EnvCard
              label="CO₂" sublabel="carbon dioxide" value={co2} display={fmtInt(co2)} unit=" ppm"
              icon={<Cloud size={18} />} colorRgb="107,63,160" {...gaugeRange('air_co2', 0, 2000)} {...threshold('air_co2', 400, 1500)}
              series={series.co2} decimals={0}
            />
            <EnvCard
              label="ความเข้มแสง" sublabel="illuminance" value={lux} display={fmtInt(lux)} unit=" lux"
              icon={<Sun size={18} />} colorRgb="133,79,11" {...gaugeRange('air_light', 0, 200000)} {...threshold('air_light')}
              series={series.lux} decimals={0}
            />
          </div>

          <HistoryChartSection greenhouseId={greenhouseId} />

          {latestDeviceTs > 0 && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500 text-right border-t border-gray-100 dark:border-gray-800 pt-2.5 mt-5">
              บันทึกล่าสุด: {new Date(latestDeviceTs).toLocaleString('th-TH')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
